import { useState, type ComponentPropsWithoutRef, type Ref } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Input } from './input';
import { cn } from '@/lib/cn';

type PasswordInputProps = Omit<ComponentPropsWithoutRef<typeof Input>, 'type'> & {
  showLabel?: string;
  hideLabel?: string;
  ref?: Ref<HTMLInputElement>;
};

/**
 * Password input with a show/hide toggle. Uses the same `aria-invalid` /
 * `aria-describedby` props forwarded from the parent (typically a
 * `<FormField>`).
 */
export const PasswordInput = ({ ref, className, showLabel = 'Show password', hideLabel = 'Hide password', disabled, ...props }: PasswordInputProps) => {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <Input
        ref={ref}
        type={visible ? 'text' : 'password'}
        className={cn('pr-10', className)}
        disabled={disabled}
        {...props}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? hideLabel : showLabel}
        aria-pressed={visible}
        disabled={disabled}
        className="absolute right-1 top-1/2 -translate-y-1/2 grid h-8 w-8 place-items-center rounded-md text-text-secondary hover:text-foreground hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 disabled:opacity-50"
      >
        {visible ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
      </button>
    </div>
  );
};
PasswordInput.displayName = 'PasswordInput';
